'use-strict';

import angular, { IHttpService } from 'angular';
import { ToastService, ToastType } from '../../ui-components/toast/toast.service';
import {
  EventMonitoringEvent,
  EventRule,
  WorkflowRulePreviewRequest,
  WorkflowRulePreviewResponse,
} from '../../src/app/@shared-types/event-monitoring';

export type ExtendedWorkflowEvent = EventMonitoringEvent & {
  icon: string,
  link: string,
  source_description: string,
};

export class MonitoringAPIService {
  private $http: IHttpService;

  private toastService: ToastService;

  constructor(
    $http: IHttpService,
    toastService: ToastService,
  ) {
    'ngInject';

    this.$http = $http;
    this.toastService = toastService;
  }

  /** Adds a new `Workflow` */
  public apiAddEventRule = async (
    workflow: EventRule,
  ): Promise<void> => this.$http
    .post<void>(`${API_URL}/api/monitoring/event-rules`, workflow)
    .then(() => {})
    .catch((e) => {
      this.apiError('Der Workflow konnte nicht angelegt werden', e);
    });

  /** Generates a `WorkflowPreview` for the supplied `Workflow`. */
  public apiGetPreview = async (
    req: WorkflowRulePreviewRequest,
  ): Promise<WorkflowRulePreviewResponse> => this.$http
    .post<WorkflowRulePreviewResponse>(`${API_URL}/api/monitoring/preview`, { rule: req })
    .then((preview) => preview.data)
    .catch((e) => {
      this.apiError('Das Ereignis konnte nicht bearbeitet werden.', e);
      throw e;
    });

  /** Update a `Workflow` */
  public apiUpdateEventRule = async (
    workflow: EventRule,
  ): Promise<void> => this.$http
    .put<void>(`${API_URL}/api/monitoring/event-rules`, workflow)
    .then(() => {})
    .catch((e) => {
      this.apiError('Der Workflow konnte nicht bearbeitet werden.', e);
      throw e;
    });

  /** Delets a `Workflow` */
  public apiDeleteEventRule = async (
    workflowId: number,
  ): Promise<void> => this.$http
    .delete<void>(`${API_URL}/api/monitoring/event-rules/${workflowId}`)
    .then(() => {})
    .catch((e) => {
      this.apiError('Der Workflow konnte nicht gelöscht werden.', e);
      throw e;
    });

  /** Returns all `Workflow`'s. */
  public apiGetEventRules = async (): Promise<EventRule[]> => this.$http
    .get<EventRule[]>(`${API_URL}/api/monitoring/event-rules`)
    .then((res) => res.data)
    .catch((e) => {
      this.apiError('Die Workflows konnten nicht abgerufen werden.', e);
      throw e;
    });

  /** Get a single `Workflow`. */
  public apiGetEventRule = async (
    workflowId: number,
  ): Promise<EventRule> => this.$http
    .get<EventRule[]>(`${API_URL}/api/monitoring/event-rules/${workflowId}`)
    .then((res) => res.data[0])
    .catch((e) => {
      this.apiError('Der Workflow konnten nicht abgerufen werden.', e);
      throw e;
    });

  /** Returns all `Event`'s for the current `User`. */
  public apiGetEvents = async (): Promise<ExtendedWorkflowEvent[]> => this.$http
    .get<EventMonitoringEvent[]>(`${API_URL}/api/monitoring/events`)
    .then((res) => this.prepareEvents(res.data))
    .catch((e) => {
      this.apiError('Die Ereignisse konnten nicht abgerufen werden.', e);
      throw e;
    });

  /** Returns all `Event`'s */
  public apiGetAllEvents = async (): Promise<ExtendedWorkflowEvent[]> => this.$http
    .get<EventMonitoringEvent[]>(`${API_URL}/api/monitoring/all-events`)
    .then((res) => this.prepareEvents(res.data))
    .catch((e) => {
      this.apiError('Die Ereignisse konnten nicht abgerufen werden.', e);
      throw e;
    });

  /** Adds additional data to `Event`'s */
  private prepareEvents = (events: EventMonitoringEvent[]): ExtendedWorkflowEvent[] => {
    const extendedEvents: ExtendedWorkflowEvent[] = [];

    for (const event of events) {
      extendedEvents.push({
        ...event,
        icon: 'fad fa-car',
        link: `/vehicles/stock?id=${event.source_id}`,
        source_description: 'Fahrzeug',
      });
    }

    return extendedEvents;
  };

  /** Show an error message and log the error stack to the console */
  private apiError = (message: string, error: Error): void => {
    this.toastService.toast(ToastType.ERROR, message);
    // eslint-disable-next-line no-console
    console.error(error);
  };
}

export default angular.module('services.monitoringApiService', [])
  .service('monitoringApiService', MonitoringAPIService)
  .name;
